import css from './Modal.module.scss'
import React, { forwardRef, ForwardRefExoticComponent, PropsWithChildren, RefAttributes, useEffect } from 'react'
import OutsideClickHandler from 'react-outside-click-handler'
import classNames from 'classnames'

import Img from 'components/Media/Img'
import DataTestId from 'meta/dataTestId'

interface IProps extends RefAttributes<HTMLElement> {
  handleClose(event?: object): void
  show: boolean
  grey?: boolean
  fullHeight?: boolean
  fullWidth?: boolean
  overflowY?: boolean
  hideCloseIcon?: boolean
  fullscreenOnMobile?: boolean
  transparent?: boolean
  dark?: boolean
  closeIconPath?: string
  noBodyFixed?: boolean
  limitDesktopWidth?: boolean
  disableScroll?: boolean
  noPadding?: boolean
}

const Modal: ForwardRefExoticComponent<IProps & PropsWithChildren> = forwardRef<HTMLElement, IProps>((props, ref) => {
  const {
    show,
    children,
    grey,
    dark,
    fullHeight,
    fullWidth,
    transparent,
    overflowY,
    handleClose,
    fullscreenOnMobile,
    hideCloseIcon,
    closeIconPath,
    noBodyFixed,
    limitDesktopWidth,
    disableScroll,
    noPadding,
  } = props

  useEffect(() => {
    return () => {
      document.body.classList.remove(css.modal_active)

      if (disableScroll) {
        document.body.style.overflow = ''
      }
    }
  }, [])

  useEffect(() => {
    if (show && disableScroll) {
      document.body.style.overflow = 'hidden'
    }

    if (show && !noBodyFixed) document.body.classList.add(css.modal_active)
    else document.body.classList.remove(css.modal_active)
  }, [show])

  return (
    <div className={css.layout_modal}>
      <div
        className={classNames(css.modal, {
          [css.show]: show,
          [css.hide]: !show,
          [css.dark]: dark,
          [css.full_height]: fullHeight,
          [css.full_width]: fullWidth,
        })}
      >
        <OutsideClickHandler onOutsideClick={handleClose}>
          <section
            className={classNames(css.modal_main, {
              [css.overflowY]: overflowY,
              [css.grey]: grey,
              [css.fullscreenOnMobile]: fullscreenOnMobile,
              [css.transparent]: transparent,
              [css.full_height]: fullHeight,
              [css.full_width]: fullWidth,
              [css.limitDesktopWidth]: limitDesktopWidth,
              [css.noBodyFixed]: noBodyFixed,
              [css.noPadding]: noPadding,
            })}
            ref={ref}
          >
            {!hideCloseIcon && (
              <div data-testid={DataTestId.modal.btnClose} className={css.close} onClick={handleClose}>
                <Img src={closeIconPath ?? '/icons/close_icon.svg'} />
              </div>
            )}

            {children}
          </section>
        </OutsideClickHandler>
      </div>
    </div>
  )
})

Modal.displayName = 'Modal'

export default Modal
